import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import { CircularProgress } from '@mui/material';

import { graphql } from '../../__generated__';
import Copyright from '../../components/common/Copyright';

export const isValidQuery = graphql(/* GraphQL */ `
  query IsValidAgent($email: String!) {
    isValidAgent(email: $email)
  }
`);

const EmailVerification = () => {
  const [formError, setFormError] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const navigate = useNavigate();

  const { data, loading } = useQuery(isValidQuery, {
    variables: { email: email },
    skip: email.length === 0,
    onCompleted: (data) => {
      if (data?.isValidAgent && email.length > 0) {
        setFormError('');
        setMessage('Email is valid and authorized for registration.');
        navigate('/registration', { state: { email } });
      } else {
        setMessage('');
        if (email.length > 0) setFormError('User not authorized.');
      }
    },
    onError: () => {
      setMessage('');
      setFormError('User not authorized.');
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    setMessage('');
    const data = new FormData(event.currentTarget);
    const email = data.get('email');

    if (typeof email === 'string') {
      setEmail(email);
    } else {
      setFormError(
        'There was an issue with the form submission. Please contact system admin.'
      );
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Email Address
        </Typography>
        {!data?.isValidAgent && formError && (
          <Alert sx={{ mt: 1 }} severity="error">
            {formError}
          </Alert>
        )}
        {message && (
          <Alert sx={{ mt: 1 }} severity="success">
            {message}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            disabled={loading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress color="inherit" /> : 'Submit'}
          </Button>
          <Grid container>
            <Grid item xs>
              <Link component={RouterLink} to={'/login'} variant="body2">
                Back to Login
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Copyright />
    </Container>
  );
};

export default EmailVerification;
